<template>
  <div id="AddcommentsMain" class="row m-0 p-0">
    <div class="col-12">
      <PageHeader
        :page="page"
        :has-save="true"
        :has-return="true"
        @onSave="saveComment"
        @onReturn="goBack"
      />

      <div class="row mx-auto">
        <div class="col-12">
          <SectionTitle content="Información General" :has-line="true" />
        </div>
      </div>

      <div class="row mx-auto">
        <div class="col-12">
          <ValidationObserver
            ref="formValidator"
            novalidate
            tag="form"
            class="row mx-auto"
          >
            <div class="col-6 p-0 pr-4">
              <span>
                <label class="field-label" for="module">Módulo:</label>
                <ValidationProvider
                  v-slot="{ errors }"
                  rules="required"
                  name="module"
                >
                  <CustomDropDown
                    v-model="filters.module"
                    placeholder="Seleccione"
                    required
                    fieldkey="id"
                    fieldname="name"
                    :options="modules"
                    :error="errors[0]"
                    :custom-error-msg="errorMsgs['selectModule']"
                    :disabled="isModuleReadOnly || isReadOnlyWithExit"
                    @input="onModuleChange"
                  />
                  <Error
                    v-if="isFormValidated && !filters?.module?.id"
                    variant="no-border"
                    :message="errorMsgs['selectModule']"
                  />
                </ValidationProvider>
              </span>
            </div>
            <div class="col-6 p-0">
              <span>
                <label class="field-label" for="category">Categoría:</label>
                <ValidationProvider
                  v-slot="{ errors }"
                  rules="required"
                  name="category"
                >
                  <CustomDropDown
                    v-model="filters.category"
                    label
                    placeholder="Seleccione"
                    required
                    fieldkey="id"
                    fieldname="name"
                    :disabled="categoryReadOnly || !filters.module?.id"
                    :options="categories"
                    :error="errors[0]"
                    :custom-error-msg="errorMsgs['selectCategory']"
                  />
                </ValidationProvider>
              </span>
            </div>

            <div class="col-12 mt-4 p-0">
              <span>
                <ValidationProvider
                  v-slot="{ errors }"
                  slim
                  rules="required"
                  name="comment"
                >
                  <label class="field-label" for="comment">Comentario:</label>
                  <custom-text-area
                    v-model="comment.description"
                    required
                    placeholder="Ingresa comentario"
                    :maxlength="3000"
                    :error="`${errors[0] ? 'error' : ''}`"
                    :custom-error-msg="errorMsgs['selectComment']"
                  />
                </ValidationProvider>
              </span>
            </div>
          </ValidationObserver>
        </div>
      </div>

      <div class="row mx-auto mt-4">
        <div class="col-12">
          <SectionTitle content="Subir Documento" :has-line="true" />
        </div>
      </div>

      <div class="row mx-auto">
        <div class="col-12">
          <div
            class="comment-attachment py-3 px-4 bg-secondary-13 d-flex justify-content-between align-items-center"
          >
            <div class="attachment-info">
              <label class="field-label">Documento</label>
              <div class="attachment-filename">
                {{ getDocumentFilename(comment) | empty }}
              </div>
            </div>
            <div class="attachment-uploader">
              <custom-file-input
                id="attachmentUpload"
                ref="attachmentUpload"
                v-model="comment.attachment"
                display-style="normal"
                :on-download="() => {}"
                :accept="'.pdf'"
                label-position="left"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SectionTitle } from "@/components/pei/shared";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import CustomDropDown from "@/components/common/CustomDropDown";
import Error from "@/components/common/Error.vue";
import Datepicker from "@/components/common/CustomDatePicker";

import CustomFileInput from "CustomFileInput";
import CustomTextArea from "@/components/common/CustomTextArea";
import alert from "@/mixin/alert.mixin";
import store from "@/store/index";
import PageHeader from "@/components/common/page/PageHeader";
import codeViewTypes from "@/utils/constants/comment/CodeViewTypes.js";
import commentTypes from "@/utils/constants/comment/commentTypes";
import studentService from "@/services/student.js";
import objectToFormData from "object-to-formdata";
import commentsStore from "@/views/comments/comments-store";
import secModules from "@/utils/constants/secModules";

export default {
  name: "AddComment",
  mixins: [alert],
  components: {
    SectionTitle,
    ValidationProvider,
    ValidationObserver,
    CustomDropDown,
    Datepicker,
    CustomTextArea,
    PageHeader,
    CustomFileInput,
    Error
  },
  data() {
    return {
      page: {
        icon: "fas fa-user-edit",
        title: "Comentarios",
        routes: ["Inicio", "Estudiante", "Comentarios"],
        sections: ["Búsqueda"]
      },
      errorMsgs: {
        selectModule: " Favor seleccionar módulo.",
        selectCategory: "Favor seleccionar categoría.",
        selectComment: " Favor registrar comentario."
      },
      filters: {
        module: null,
        category: null
      },
      isReadOnlyWithExit: false,
      comment: {
        id: null,
        studentSieId: parseInt(store.getters["$_student/StudentSie"]),
        date: new Date(),
        categoryTypeId: 0,
        description: "",
        userId: store.state.userProfile.sub,
        attachment: null
      },
      categories: [],
      categoryList: [],
      isFormValidated: false
    };
  },
  props: {
    onCommentSaved: {
      type: Function,
      required: false,
      default: null
    }
  },
  computed: {
    modules() {
      return commentsStore.getters.modules;
    },
    rootParams() {
      return this.$root?.$route?.params;
    },
    isEditing() {
      return this.comment.id > 0;
    },
    categoryTypesNotEditable() {
      if (!this.isEditing) return [];

      return [
        commentTypes.RegistroComentarioInactividad,
        commentTypes.RegistroSeguimiento,
        commentTypes.RegistroReactivacion,
        commentTypes.EgresoEliminarEgreso,
        commentTypes.TransportacionBecas
      ];
    },
    isModuleReadOnly() {
      return this.isReadOnlyWithExit || Boolean(this.filters.module?.id);
    },
    categoryReadOnly() {
      const mustBeReadOnly =
        this.categoryTypesNotEditable.indexOf(this.comment.categoryTypeId) !==
        -1;

      return this.isReadOnlyWithExit || mustBeReadOnly;
    },
    getDocumentFilename() {
      return comment => comment.filename || comment.attachment?.name;
    }
  },
  methods: {
    syncReadonlyStateFromRouteForInit() {
      if (!this.rootParams) return;
      if (this.rootParams.commentId) {
        this.comment.id = this.rootParams.commentId;
      } else {
        this.isReadOnlyWithExit = Boolean(this.rootParams.isReadOnly);
        if (this.rootParams.commentCategoryTypeId) {
          this.comment.categoryTypeId = this.rootParams.commentCategoryTypeId;
        }
      }
    },
    async fetchCommentData() {
      const isValidComment = this.comment.id > 0;
      if (!isValidComment) return;
      try {
        const res = await this.$ApiGet(
          "/api/student/getcomment/" + this.comment.id
        );
        this.comment = res.data;
        this.comment.userId = store.state.userProfile.sub;
        await this.initFilters();
        this.categoryList = [...commentsStore.getters.categoriesCatalogue];
        this.filters.module = { ...commentsStore.getters.selectedModule };
        if (this.filters.module) {
          this.onModuleChange(this.filters.module);
        }
        this.filters.category = this.categories.find(
          f => f.id == this.comment.categoryTypeId
        );
      } catch (err) {
        this.ShowToast(err.message, "", "error");
      }
    },
    goBack() {
      this.$router.back();
    },
    onModuleChange(selectedItem) {
      if (selectedItem?.id) {
        this.categories = (this.categoryList || []).filter(
          x => x.moduleId == selectedItem.id
        );
      } else {
        this.categories = [];
      }

      this.filters.category = null;
    },
    async validate() {
      this.isFormValidated = true;
      return await this.$refs.formValidator.validate();
    },
    async saveComment() {
      let valid = await this.validate();

      if (!valid || !this.filters?.module?.id || !this.filters?.category?.id)
        return;

      this.comment.categoryTypeId = this.filters.category.id;

      let formData = objectToFormData({ ...this.comment }, { indices: true });

      if (this.comment?.attachment) {
        formData.append("attachment", this.comment.attachment);
      }

      try {
        const { data } = await studentService.storeComment(formData);

        this.comment.id = data.id;

        if (data?.id) {
          this.ShowToast(
            "Proceso exitoso",
            "El comentario fue guardado exitosamente",
            "success"
          );
        }

        store.getters.SelectedStudent.status = data.studentStatus;
        this.fetchCommentData();

        this.$emit("comment-saved", this.comment);

        if (
          this.rootParams.codeViewTypeId &&
          this.rootParams.codeViewTypeId == codeViewTypes.EgresarEstudiante
        ) {
          let exit = store.getters.getExit;

          exit.saved = true;
          store.dispatch("setExit", exit);
          this.$router.push({ name: exit.pathName });
          return;
        }

        if (this.onCommentSaved) {
          this.onCommentSaved(this.comment);
        } else {
          this.goBack();
        }
      } catch (err) {
        this.ShowToast(err.message, "", "error");
      }
    },
    loadCommentDataFromRoute() {
      if (this.rootParams) {
        this.comment.id = this.rootParams.commentId;
        if (this.comment.id > 0) {
          this.fetchCommentData();
        } else {
          this.isReadOnlyWithExit = this.rootParams.isReadOnly;

          if (this.rootParams.commentCategoryTypeId) {
            this.filters.category = this.categories.find(
              c => c.id === this.rootParams.commentCategoryTypeId
            );
            this.comment.categoryTypeId = this.filters.category?.id;
          }
        }
      }
    },
    async initFilters() {
      this.syncReadonlyStateFromRouteForInit();

      const studentSieId =
        this.$route.params.studentSieId ??
        this.$store.getters.SelectedStudent.studentId;
      const categoryId =
        this.$route.params.categoryId ?? this.rootParams.commentCategoryTypeId;

      const payload = {
        categoryId: categoryId,
        studentSieId: studentSieId,
        enabledToAdd: true,
        categoryReadOnly: this.categoryReadOnly
      };

      const viewTypes = [
        codeViewTypes.EgresarEstudiante,
        codeViewTypes.InactivarEstudiante
      ];
      if (viewTypes.includes(this.rootParams.codeViewTypeId)) {
        payload.moduleId = secModules.student; // Módulo de Estudiante
      }

      await commentsStore.dispatch("init", payload);
    }
  },
  async created() {
    await this.initFilters();
  },
  async mounted() {
    await this.initFilters();
    this.loadCommentDataFromRoute();

    this.categoryList = [...commentsStore.getters.categoriesCatalogue];
    this.filters.module = { ...commentsStore.getters.selectedModule };

    if (this.filters.module) {
      this.onModuleChange(this.filters.module);
    }

    this.filters.category = { ...commentsStore.getters.selectedCategory };
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_colors.scss";

.field-label {
  font-size: 14px;
  color: map-get($colors, "gray-6");
}
.field-data {
  font-size: 16px;
}
:deep(.char-count) {
  float: right;
  font-size: 12px;
  color: map-get($colors, "gray-6");
}
</style>
